import { useDebouncedCallback } from "use-debounce";
import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import {
  Button,
  CustomTableMenuItem,
  FilterDefinition,
  Loader,
  Operators,
  RuleGroup,
  SelectionState,
  SelectionStatus,
  Table,
  TableInstance,
} from "@monolith-forensics/monolith-ui";
import type { ButtonProps } from "@monolith-forensics/monolith-ui";
import type { WithTestId } from "@/types/testid";

import { StorageAPI } from "../storage.api";
import CaseStorageColumnsDefs from "@/components/CaseStorage/CaseStorageColumnsDefs";
import { addCustomFieldsToColumnDefs } from "@/components/Evidence/CustomFieldCell";
import { getDateFormat } from "@/utils/date-format";
import { usePermissions } from "@/hooks/usePermissions";
import { useTableState } from "@/Features/_shared";
import { CreateStorageModal } from "../Modals/CreateStorageModal";
import { StorageFlyout } from "../Flyout/StorageFlyout";
import { storageQueryKeys } from "../storage.query";
import { useStorageColumnDefinitions } from "./StorageColumnDefinitions";
import { useStorageFilterDefinitions } from "./StorageFilterDefinitions";
import { resolveStorageQuery } from "./resolveStorageQuery";
import {
  GetStorageQuery,
  StorageBulkActionItem,
  StorageItem,
  StorageTableQuery,
} from "./types";

const DEFAULT_PAGE_SIZE = 100;

export type StorageTableHandle = {
  refresh: () => void;
  clearSelections: () => void;
  getSelectionState: () => SelectionState | undefined;
  /** The rows loaded so far (every fetched page, flattened). */
  getRecords: () => StorageItem[];
  /** The query driving the current fetch, with the filter sanitized. */
  getResolvedQuery: () => GetStorageQuery;
  getFilterDefinitions: () => FilterDefinition[];
} & Omit<Partial<TableInstance>, "clearSelections">;

/**
 * The part of the query that decides which rows match (filter, search, the
 * caller's scope). Sort and page size only reorder or chunk the same set.
 */
const getMembershipKey = ({
  sort_by: _sortBy,
  sort_dir: _sortDir,
  pageSize: _pageSize,
  search,
  ...membership
}: GetStorageQuery) =>
  // An empty search matches the same rows as no search.
  JSON.stringify({ ...membership, search: search || undefined });

/**
 * Drops any saved selection from the persisted table blob before
 * `useTableState` and the monolith-ui Table read it on mount. The blob does
 * not record the caller's scope, and a shared key can follow the user from one
 * case to another, so a restored selection (a select all, or explicit rows
 * from another case) could arm a bulk action over rows the user never picked
 * here. Must run above `useTableState`.
 */
const useDropSavedSelection = (stateStoreKey?: string) => {
  useState(() => {
    if (!stateStoreKey) return null;
    try {
      const raw = localStorage.getItem(stateStoreKey);
      if (!raw) return null;
      const persisted = JSON.parse(raw);
      if (persisted?.selectionState) {
        delete persisted.selectionState;
        localStorage.setItem(stateStoreKey, JSON.stringify(persisted));
      }
    } catch (error) {
      console.error("error", error);
    }
    return null;
  });
};

/**
 * Strips empty/incomplete rules from the mfui filter RuleGroup before it drives
 * a fetch — mirrors EvidenceTable so half-entered filters don't query.
 */
const resolveFilterState = (filterState: RuleGroup | undefined) => {
  if (!filterState) return undefined;
  if (filterState.rules.length > 0) {
    const filteredRules = filterState.rules.filter((rule) => {
      if (
        [Operators.IsEmpty.value, Operators.IsNotEmpty.value].includes(
          rule.operator.value
        )
      ) {
        return true;
      }
      // An empty value array (multiselect with everything unchecked) must be
      // treated as incomplete: the backend rewrites empty-valued conditions to
      // `(field IS NULL OR field = '')`, silently inverting "no selection"
      // into an "is empty" filter.
      return (
        !!rule.value &&
        rule.value.length > 0 &&
        !rule.value.some((v) => v === undefined || v === null || v === "")
      );
    });
    if (filteredRules.length > 0) {
      return { ...filterState, rules: filteredRules };
    }
  }
  return undefined;
};

export const StorageTable: React.FC<{
  tableRef?: React.MutableRefObject<StorageTableHandle | undefined>;
  query?: StorageTableQuery;
  stateStoreKey?: string;
  onAdd?: () => void;
  onLoaded?: () => void;
  onActionButtonClick?: (rowData: StorageItem) => void;
  showSelection?: boolean;
  enableActions?: boolean;
  /**
   * Items for the left "Actions" menu, shown only while one or more rows are
   * selected and at least one item is visible.
   */
  bulkActionItems?: StorageBulkActionItem[];
  /**
   * Extra toolbar items, shown whether or not anything is selected. Items with
   * no `position` go on the right.
   */
  toolbarItems?: CustomTableMenuItem[];
  onSelectionChange?: (selectionState: SelectionState) => void;
}> = ({
  tableRef,
  query = {},
  stateStoreKey = "storage:table",
  onAdd,
  onLoaded,
  onActionButtonClick,
  showSelection = false,
  enableActions = true,
  bulkActionItems,
  toolbarItems,
  onSelectionChange,
}) => {
  const { hasPermission, MONOLITH_PERMISSIONS } = usePermissions();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const tableInstanceRef = useRef<TableInstance>();

  const [focusedRowData, setFocusedRowData] = useState<StorageItem | null>(
    null
  );
  const [showFlyout, setShowFlyout] = useState(false);
  const [showCreatePopup, setShowCreatePopup] = useState(false);

  const filterDefinitions = useStorageFilterDefinitions();
  const {
    columnDefinitions,
    customFields,
    isSuccess: isColumnDefinitionsReady,
    isError: isColumnDefinitionsError,
    refetch: refetchColumnDefinitions,
  } = useStorageColumnDefinitions();

  useDropSavedSelection(stateStoreKey);
  const tableState = useTableState<GetStorageQuery>(
    query as GetStorageQuery,
    stateStoreKey,
    DEFAULT_PAGE_SIZE
  );

  // useTableState restores the persisted filter state raw (mfui saves rules
  // mid-edit, incl. empty/partial values), so re-apply resolveFilterState here —
  // otherwise a half-entered saved rule drives the first fetch with
  // undefined/empty bind values. The live path is already sanitized in
  // onFilterChange; this is idempotent for it.
  const resolvedQuery = useMemo<GetStorageQuery>(
    () => ({
      ...tableState.resolvedQuery,
      filter: resolveFilterState(tableState.resolvedQuery.filter),
    }),
    [tableState.resolvedQuery]
  );

  // A select-all / exclusion selection means "everything matching the query
  // at selection time", and a bulk action re-resolves it against the query it
  // is sent with. If the filter or search changes while such a selection is
  // held, the action would hit a set the user never saw, so drop it. Explicit
  // row selections are uuid snapshots and survive a filter or search change.
  // A change of the caller's scope (the `query` prop, e.g. another case) drops
  // every selection: explicit rows from the old scope are not on screen in the
  // new one. Sort and page size don't change which rows match, so they are
  // left out of the comparison.
  const membershipKey = getMembershipKey(resolvedQuery);
  const scopeKey = JSON.stringify(query);
  const previousKeys = useRef({ membershipKey, scopeKey });
  useEffect(() => {
    const previous = previousKeys.current;
    if (
      previous.membershipKey === membershipKey &&
      previous.scopeKey === scopeKey
    ) {
      return;
    }
    previousKeys.current = { membershipKey, scopeKey };
    const selectionStatus = (
      tableInstanceRef.current?.getTableState().selectionState ??
      tableState.selectionState
    )?.selectionStatus;
    const scopeChanged = previous.scopeKey !== scopeKey;
    if (
      selectionStatus === SelectionStatus.All ||
      selectionStatus === SelectionStatus.SomeExcluded ||
      (scopeChanged &&
        !!selectionStatus &&
        selectionStatus !== SelectionStatus.None)
    ) {
      tableInstanceRef.current?.clearSelections();
    }
  }, [membershipKey, scopeKey, tableState.selectionState]);

  const queryKey = useMemo(
    () => storageQueryKeys.infiniteList(resolvedQuery),
    [resolvedQuery]
  );

  const {
    data,
    isLoading,
    isFetched,
    isFetchingNextPage,
    hasNextPage,
    refetch,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam }) =>
      StorageAPI.query(
        resolveStorageQuery({
          resolvedQuery,
          page: pageParam as number,
          filterDefinitions,
        })
      ),
    getNextPageParam: (lastPage) => lastPage.nextPage,
    initialPageParam: 1,
    // Wait for the custom-field definitions: a persisted custom-field date
    // filter resolved before they load would be typed "text" (wrong SQL path)
    // and stay cached, since the definitions are not part of the queryKey.
    // isSuccess (not isFetched) so an errored definitions fetch fails closed
    // instead of opening the gate with an empty type map.
    enabled: isColumnDefinitionsReady,
    placeholderData: (previous) => previous,
  });

  const records = useMemo(
    () =>
      data?.pages?.reduce<StorageItem[]>((acc, page) => {
        return [...acc, ...page.data];
      }, []) || [],
    [data?.pages]
  );

  // The flyout renders its detail list from the LEGACY column defs (its
  // `render(rowData, opts)` signature differs from the mfui columns'), so feed
  // it the legacy defs + custom fields rather than the mfui table columns.
  const flyoutColumnDefs = useMemo(
    () =>
      customFields
        ? addCustomFieldsToColumnDefs(customFields.data, CaseStorageColumnsDefs)
        : CaseStorageColumnsDefs,
    [customFields]
  );

  // Guard against repeated scroll-to-bottom events: an unconditional
  // fetchNextPage() cancels and restarts the in-flight page request
  // (cancelRefetch defaults to true), so continued scrolling can starve the
  // next page indefinitely.
  const debouncedFetchNextPage = useDebouncedCallback(() => {
    if (hasNextPage && !isFetchingNextPage) fetchNextPage();
  }, 50);

  const debouncedSearch = useDebouncedCallback((value) => {
    tableState.setSearchState(value);
  }, 300);

  const handleExportTable = useCallback(
    (option: string) => {
      let columns = (tableInstanceRef.current?.columnState ?? [])
        .filter((c) => c.dataField !== "Action")
        .map((c) => ({
          dataField: c.dataField,
          header: c.caption || c.dataField,
          caption: c.caption || c.dataField,
          visible: c.visible,
          dataType: c.dataType,
        }));

      if (option === "export:visible") {
        columns = columns.filter((c) => c.visible !== false);
      }

      const { query: legacyQuery } = resolveStorageQuery({
        resolvedQuery,
        page: 1,
        filterDefinitions,
      });

      enqueueSnackbar("Exporting table...", { variant: "info" });

      StorageAPI.exportStorageList({
        query: legacyQuery,
        type: "xlsx",
        columns,
        date_format: getDateFormat({ isMoment: true, includeTime: true }),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        custom_date_format: getDateFormat({
          isMoment: true,
          includeTime: false,
        }),
      })
        .then((res) => {
          const { signedUrl, filename } = res;
          const el = document.createElement("a");
          el.href = signedUrl.replace(
            "http://localhost:3000",
            "http://localhost:3001"
          );
          el.download = filename;
          el.click();
          el.remove();
        })
        .catch(() => {
          enqueueSnackbar("Failed to export storage list. Please try again.", {
            variant: "error",
          });
        });
    },
    [resolvedQuery, filterDefinitions, enqueueSnackbar]
  );

  const handleActionButtonClick = (rowData: StorageItem) => {
    if (onActionButtonClick) {
      onActionButtonClick(rowData);
      return;
    }
    setFocusedRowData(rowData);
    setShowFlyout(true);
  };

  const handleGetNextItem = (uuid: string) => {
    if (!uuid || records.length === 0) return;
    const current = records.findIndex((i) => i.uuid === uuid);
    if (current === -1) return;
    setFocusedRowData(records[current + 1] || records[0]);
  };

  const handleGetPrevItem = (uuid: string) => {
    if (!uuid || records.length === 0) return;
    const current = records.findIndex((i) => i.uuid === uuid);
    if (current === -1) return;
    setFocusedRowData(records[current - 1] || records[records.length - 1]);
  };

  const handleDeleteFromCache = (deletedItem?: StorageItem) => {
    queryClient.setQueryData(queryKey, (old: any) => {
      if (!old) return old;
      return {
        ...old,
        pages: old.pages.map(
          (page: { data: StorageItem[]; total: number }) => ({
            ...page,
            data: page.data.filter((item) => item.uuid !== deletedItem?.uuid),
            total: Math.max(0, (page.total || 0) - 1),
          })
        ),
      };
    });
  };

  useImperativeHandle(
    tableRef,
    () => ({
      ...tableInstanceRef.current,
      refresh: () => {
        refetch();
      },
      clearSelections: () => {
        tableInstanceRef.current?.clearSelections();
      },
      getSelectionState: () =>
        tableInstanceRef.current?.getTableState().selectionState ??
        tableState.selectionState,
      getRecords: () => records,
      getResolvedQuery: () => resolvedQuery,
      getFilterDefinitions: () => filterDefinitions,
    }),
    [
      refetch,
      tableState.selectionState,
      records,
      resolvedQuery,
      filterDefinitions,
    ]
  );

  const loading = isLoading || !isFetched || isFetchingNextPage;

  useLayoutEffect(() => {
    if (isFetched) onLoaded?.();
  }, [isFetched, onLoaded]);

  // Same gate as EvidenceTable's showBulkActions: the Actions menu only makes
  // sense with something selected. The global list passes no items, so it
  // never shows there.
  const totalSelected = tableState.selectionState?.totalSelected ?? 0;
  const visibleBulkActionItems = (bulkActionItems ?? []).filter(
    (item) => item.visible !== false
  );
  const customMenuItems: CustomTableMenuItem[] = [
    ...(totalSelected > 0 && visibleBulkActionItems.length > 0
      ? [
          {
            type: "menu" as const,
            position: "left" as const,
            options: {
              children: "Actions",
              data: visibleBulkActionItems,
              arrow: true,
            },
          },
        ]
      : []),
    // monolith-ui puts an item with no position on the left; toolbar items
    // belong on the right so they don't shift when the Actions menu appears.
    ...(toolbarItems ?? []).map((item) => ({
      ...item,
      position: item.position ?? ("right" as const),
    })),
  ];

  // The query gate fails closed on a definitions error (see `enabled` above),
  // so without this branch an errored fetch would leave a permanent <Loader />.
  if (isColumnDefinitionsError) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 12,
          height: "100%",
          minHeight: 200,
        }}
      >
        <div>Failed to load storage columns. Please try again.</div>
        <Button
          size="sm"
          variant="outlined"
          onClick={() => refetchColumnDefinitions()}
        >
          Retry
        </Button>
      </div>
    );
  }

  if (!isColumnDefinitionsReady) {
    return <Loader />;
  }

  return (
    <>
      {showFlyout && focusedRowData && (
        <StorageFlyout
          flyoutData={focusedRowData}
          setFlyoutData={setFocusedRowData}
          setIsFlyoutVisible={setShowFlyout}
          handleGetNextItem={handleGetNextItem}
          handleGetPrevItem={handleGetPrevItem}
          columnState={flyoutColumnDefs}
          handleRefresh={() => refetch()}
          onDelete={handleDeleteFromCache}
        />
      )}
      <CreateStorageModal
        show={showCreatePopup}
        onClose={() => setShowCreatePopup(false)}
        onCancel={() => setShowCreatePopup(false)}
        caseInfo={{ case_id: query?.case_id ?? null }}
        onSubmit={() => {
          // the modal closes itself via its own onClose
          refetch();
        }}
      />
      <Table
        data={records}
        columnProps={columnDefinitions}
        keyField="uuid"
        virtualized={true}
        tableInstanceRef={tableInstanceRef}
        totalRecords={data?.pages?.[0]?.total || 0}
        loading={loading}
        focusedRowId={focusedRowData?.uuid}
        enableSelection={showSelection}
        enableActionButton={enableActions}
        stateStorage={{
          enabled: !!stateStoreKey,
          type: "localStorage",
          key: stateStoreKey || "",
        }}
        onSelectionChange={(selection) => {
          tableState.setSelectionState(selection);
          onSelectionChange?.(selection);
        }}
        onActionButtonClick={handleActionButtonClick}
        onScrollToBottom={debouncedFetchNextPage}
        onColumnHeaderClick={({ sort }) =>
          tableState.setSortState(sort || undefined)
        }
        enableSorting={true}
        enableColumnReorder={true}
        enableColumnResize={true}
        manualSearch={true}
        manualSorting={true}
        manualFiltering={true}
        manualExport={true}
        tableMenuOptions={{
          enabled: true,
          addButtonOptions: {
            enabled: hasPermission(MONOLITH_PERMISSIONS.STORAGE_CREATE),
            label: "+ New Storage",
            // Deliberate twin with pages/Storage/StorageSection.tsx: same
            // testid on purpose; the id names the "new storage" contract.
            // Don't rename one side (Testing Philosophy doc).
            buttonOptions: {
              "data-testid": "storage-new-button",
            } as WithTestId<ButtonProps>,
            onClick: onAdd || (() => setShowCreatePopup(true)),
          },
          filterOptions: {
            enabled: true,
            showCombinator: false,
            filterDefinitions,
            onFilterChange: (filterQuery) => {
              tableState.setFilterState(resolveFilterState(filterQuery));
            },
          },
          tableCountOptions: {
            recordsTotalEnabled: true,
            selectionTotalEnabled: showSelection,
          },
          searchOptions: {
            enabled: true,
            placeholder: "Search Storage",
            onSearch: debouncedSearch,
          },
          columnSelectorOptions: {
            enabled: true,
          },
          exportOptions: {
            enabled: true,
            onExport: handleExportTable,
          },
          customMenuItems,
        }}
      />
    </>
  );
};
